import Component from '@glimmer/component';
import EmberObject, { action, get } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import GoalValidations from 'scaleflow/validators/goal';
import TargetValidations from 'scaleflow/validators/target';
import { Changeset } from 'ember-changeset';
import { assert } from '@ember/debug';

export default class GoalsWizardEditComponent extends Component {
  @service router;
  @service store;
  @service betaToggle;
  @service intl;

  goalValidations = GoalValidations;
  targetValidations = TargetValidations;

  @tracked activeStep = 0;
  @tracked goalChangeset = EmberObject.create({});
  @tracked targetChangesets = [];
  targetChangesetFactory;
  @tracked selectedGoals = [];
  @tracked selectedMetrics = [];

  @tracked goalSearch = '';
  @tracked metricSearch = '';

  @tracked showDeleteModal = false;
  @tracked selectedGoal = null;

  @tracked errorMessage = '';

  get goal() {
    return this.args.goal;
  }

  @action
  setActiveStep(index) {
    this.activeStep = index;
  }

  get activePeopleInOrganisation() {
    return this.store.peekAll('person').filter((p) => p.active);
  }

  get activeGoalsInOrganisation() {
    return this.store.peekAll('goal').filter((g) => !g.isNew);
  }

  get metrics() {
    return this.store.peekAll('metric');
  }

  get personsList() {
    return this.store.peekAll('person').filter((p) => p.active);
  }

  get goals_without_parents() {
    return this.activeGoalsInOrganisation.filter(
      (goal) => !goal.parent.content,
    );
  }

  @action
  handleOwnerSelect(changeset, item) {
    changeset.set('owner', item);
  }

  @action
  clearOwner(changeset) {
    changeset.set('owner', null);
  }

  @action
  handleStatusSelect(changeset, val) {
    changeset.set('active', val);
  }

  @action
  onDescriptionChange(changeset, e) {
    changeset.set('description', e);
  }

  @action
  goToListPage() {
    this.router.transitionTo('business.goals.list');
  }

  @action
  closeWizard() {
    if (this.args.closeWizard && typeof this.args.closeWizard === 'function') {
      this.args?.closeWizard?.();
    } else {
      assert(
        "Please pass a prop named 'closeWizard' with a callback function to the Goals Wizard component",
      );
    }
  }

  @action
  cancelForm() {
    if (this.showDeleteModal) return;
    this.goalChangeset?.rollback?.();
    this.targetChangesets.forEach((target) => {
      target.rollback();
    });
    this.closeWizard();
  }

  @action
  rollbackChanges(changeset) {
    changeset.rollback();
  }

  @action
  handleGoalsSelect(goal, e) {
    const checked = e.target.checked;
    if (checked === undefined) return;
    if (checked) this.selectedGoals = [goal];
    else this.selectedGoals = [];
  }

  @action
  unlinkParentGoal() {
    this.selectedGoals = [];
    this.goalChangeset.set('parent', null);
  }

  get targets() {
    return this.goal.targets.content ?? [];
  }

  @action
  handleMetricSelect(metric, e) {
    const checked = e.target.checked;
    if (checked === undefined) return;
    const metrics = this.selectedMetrics;
    const targetChangesets = this.targetChangesets;
    if (checked) {
      metrics.push(metric);
      const existingTarget = this.targets.find((t) => {
        return t.metric.id === metric.id;
      });
      const targetChangeset = this.targetChangesetFactory(existingTarget);
      targetChangesets.push(targetChangeset);
    } else {
      const index = metrics.findIndex((_metric) => _metric.id === metric.id);
      if (index < 0) return;
      else {
        metrics.splice(index, 1);
        targetChangesets.splice(index, 1);
      }
    }
    this.selectedMetrics = metrics;
    this.targetChangesets = targetChangesets;
  }

  @action
  async deleteDeselectedTargets() {
    const old_targets_by_metric_id = this.targets.reduce((acc, item) => {
      acc[item.metric.get('id')] = item;
      return acc;
    }, {});

    const current_metrics = this.selectedMetrics.reduce((acc, item) => {
      acc[item.id] = true;
      return acc;
    }, {});

    ///loop over old metrics, check if that metric is in the new list of metrics, if not delete the target linked to that metric
    const promises = [];
    const old_metrics = Object.keys(old_targets_by_metric_id);
    for (let i = 0; i < old_metrics.length; i++) {
      if (old_metrics[i] in current_metrics) continue;
      else
        promises.push(old_targets_by_metric_id[old_metrics[i]].destroyRecord());
    }
    await Promise.all(promises);
  }

  @action
  async saveGoal() {
    const goal = this.goal;
    goal.mapChangesetToAttributes(this.goalChangeset.changes);
    if (this.selectedGoals.length) goal.set('parent', this.selectedGoals[0]);
    await goal.save();
    await this.deleteDeselectedTargets();
    const promises = [];
    for (let i = 0; i < this.targetChangesets.length; i++) {
      const target = this.targetChangesets[i];
      target.set('goal', goal);
      target.set('metric', this.selectedMetrics[i]);
      promises.push(target.save());
    }
    await Promise.all(promises);
    this.closeWizard();
  }

  @action
  isInvalid(changeset) {
    return !changeset.get('title');
  }

  @action
  toggleDeleteModal(flag) {
    if (flag === undefined) this.showDeleteModal = !this.showDeleteModal;
    else this.showDeleteModal = flag;
  }

  @action
  confirmDeleteGoal() {
    this.selectedGoal = this.goal;
    this.toggleDeleteModal(true);
  }

  @action
  deleteGoal() {
    if (this.selectedGoal) {
      this.selectedGoal
        .destroyRecord()
        .then(() => {
          this.goToListPage();
          this.handleDismissDeleteModal();
        })
        .catch((err) => {
          const error = err.errors?.[0];
          if (!error?.detail) return;
          const message = JSON.parse(error.detail)?.[0]?.[1];
          if (!message) return;
          this.errorMessage = message;
        });
    }
  }

  @action
  handleAlertClose() {
    this.errorMessage = '';
  }

  @action
  unselectGoal() {
    this.selectedGoal = null;
  }

  @action
  handleDismissDeleteModal() {
    this.unselectGoal();
    this.toggleDeleteModal(false);
  }

  @action
  goalValidator({ key, newValue, oldValue, changes, content }) {
    let validator = get(this.goalValidations, key);
    if (typeof validator === 'function') {
      return validator(newValue, oldValue, changes, content);
    }
  }

  @action
  targetValidator({ key, newValue, oldValue, changes, content }) {
    let validator = get(this.targetValidations, key);
    if (typeof validator === 'function') {
      return validator(newValue, oldValue, changes, content);
    }
  }

  @action
  initializeForm() {
    const goalChangeset = Changeset(
      this.goal.changeset_object,
      this.goalValidator,
    );
    const targetChangesetFactory = (targetModel = undefined) => {
      const target = targetModel ?? this.store.createRecord('target', {});
      return Changeset(target, this.targetValidator);
    };

    this.goalChangeset = goalChangeset;
    this.selectedGoals = this.goal.parent?.content
      ? [this.goal.parent.content]
      : [];
    this.selectedMetrics = this.goal.metrics;
    this.targetChangesetFactory = targetChangesetFactory;
    const targets = this.goal.targets.content ?? [];
    const targetChangesets = targets.map((target) => {
      return Changeset(target, this.targetValidator);
    });
    this.targetChangesets = targetChangesets;
  }
}
